import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Cpu,
  Loader2,
  Rocket,
  ShieldCheck,
  Sparkles,
  Wallet,
} from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

import { api } from "@/lib/api";

const ICON_MAP = {
  wallet: Wallet,
  "shield-check": ShieldCheck,
  cpu: Cpu,
  rocket: Rocket,
  sparkles: Sparkles,
};

/**
 * R2-Phase-3 M4.2 — Persona picker for narration generation.
 *
 * Reads the persona manifest from /api/personas (cached 5min).  The
 * `value` is the persona key (or "default" for "no persona injection").
 * Selection is persisted to localStorage["eda.persona"] by the parent.
 *
 * Renders both a Select (key/label) and a "Sample opening" preview block
 * so presenters can taste-test a persona without an LLM call.
 */
export default function PersonaPicker({ value, onChange, disabled }) {
  const personasQuery = useQuery({
    queryKey: ["personas"],
    queryFn: async () => (await api.get("/personas")).data,
    staleTime: 5 * 60_000,
  });

  const items = personasQuery.data?.items || [];
  const selectedKey = value || "default";
  const current = items.find((p) => p.key === selectedKey);

  return (
    <div className="space-y-1.5" data-testid="persona-picker">
      <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        Audience persona
      </Label>
      <Select
        value={selectedKey}
        onValueChange={(v) => onChange(v === "default" ? null : v)}
        disabled={disabled || personasQuery.isLoading}
      >
        <SelectTrigger
          className="h-9 text-xs"
          data-testid="persona-picker-trigger"
        >
          {personasQuery.isLoading ? (
            <span className="flex items-center gap-1.5 text-slate-500">
              <Loader2 size={12} className="animate-spin" /> Loading…
            </span>
          ) : (
            <SelectValue placeholder="Default" />
          )}
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="default" data-testid="persona-option-default">
            <span className="flex items-center gap-2">
              <Sparkles size={13} className="text-slate-500" />
              Default
              <span className="text-[10px] text-slate-400">(no persona)</span>
            </span>
          </SelectItem>
          {items.map((p) => {
            const Icon = ICON_MAP[p.icon] || Sparkles;
            return (
              <SelectItem
                key={p.key}
                value={p.key}
                data-testid={`persona-option-${p.key}`}
              >
                <span className="flex items-center gap-2">
                  <Icon size={13} className="text-indigo-600" />
                  {p.label}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      {/* Sample opening preview — no LLM call required */}
      {current?.sample_opening && (
        <p
          className="rounded-md border border-indigo-100 bg-indigo-50/50 px-2.5 py-1.5 text-[11px] italic text-indigo-900"
          data-testid="persona-sample-opening"
        >
          {current.sample_opening}
        </p>
      )}
      {!current && (
        <p className="text-[11px] text-slate-400">
          Generic narration — no audience-specific framing.
        </p>
      )}
    </div>
  );
}

/**
 * Inline pill for displaying which persona generated a narration version.
 * Used in NarrationPanel's "Version X" line.
 */
export function PersonaBadge({ personaKey, items = [] }) {
  if (!personaKey) return null;
  const p = items.find((x) => x.key === personaKey);
  const Icon = (p && ICON_MAP[p.icon]) || Sparkles;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700"
      data-testid={`persona-badge-${personaKey}`}
      title={p ? `Generated for: ${p.label}` : `Persona: ${personaKey}`}
    >
      <Icon size={9} strokeWidth={2.25} />
      {p?.label || personaKey}
    </span>
  );
}
